import type { CSSProperties } from "react";
import { useEffect, useEffectEvent } from "react";

import { isUserInput } from "@skilitics/designer-ui-components";
import { getOffsetOriginConfig, parseCssUnitValue } from "@skilitics/drag-drop";

import { useEditingCss } from "./EditingCssPropertiesContext.ts";

type Props = {
  isAbsolutelyPositioned: boolean;
};

type Axis = "horizontal" | "vertical";

type ArrowKeyConfig = { axis: Axis; sign: 1 | -1 };

const arrowKeyConfigs: Record<string, ArrowKeyConfig> = {
  ArrowUp: { axis: "vertical", sign: -1 },
  ArrowDown: { axis: "vertical", sign: 1 },
  ArrowLeft: { axis: "horizontal", sign: -1 },
  ArrowRight: { axis: "horizontal", sign: 1 },
};

type InsetProperty = "top" | "right" | "bottom" | "left";

const getOffsetProperties = (axis: Axis): { startProperty: InsetProperty; endProperty: InsetProperty } =>
  axis === "horizontal"
    ? { startProperty: "left", endProperty: "right" }
    : { startProperty: "top", endProperty: "bottom" };

const nudgeInsetValue = (current: string | undefined, delta: number): string | undefined => {
  const { value, unit } = parseCssUnitValue(current ?? "0px");
  if (unit !== "px" && unit !== "") return undefined;
  return `${Math.round(value + delta)}px`;
};

const useArrowKeyPositionNudge = ({ isAbsolutelyPositioned }: Props) => {
  const { cssProperties, layoutSettings, onCssPropertiesChange } = useEditingCss();

  const handleKeyDown = useEffectEvent((event: KeyboardEvent) => {
    if (!isAbsolutelyPositioned || isUserInput(event.target)) return;

    const arrowKey = arrowKeyConfigs[event.key];
    if (!arrowKey) return;

    event.preventDefault();

    const step = event.shiftKey ? 10 : 1;
    const delta = arrowKey.sign * step;
    const config = getOffsetOriginConfig(layoutSettings.offsetOrigin ?? "topLeft");

    const isCentered = arrowKey.axis === "horizontal" ? config.horizontalCenter : config.verticalCenter;
    const property: InsetProperty | undefined =
      arrowKey.axis === "horizontal" ? config.horizontalProperty : config.verticalProperty;
    const { startProperty, endProperty } = getOffsetProperties(arrowKey.axis);

    const insetUpdates: CSSProperties = {};

    if (isCentered) {
      const startUpdate = nudgeInsetValue(cssProperties[startProperty] as string | undefined, delta);
      const endUpdate = nudgeInsetValue(cssProperties[endProperty] as string | undefined, -delta);
      if (startUpdate !== undefined) insetUpdates[startProperty] = startUpdate;
      if (endUpdate !== undefined) insetUpdates[endProperty] = endUpdate;
    } else if (property) {
      const sign = property === endProperty ? -1 : 1;
      const update = nudgeInsetValue(cssProperties[property] as string | undefined, sign * delta);
      if (update !== undefined) insetUpdates[property] = update;
    }

    if (Object.keys(insetUpdates).length > 0) {
      onCssPropertiesChange(insetUpdates);
    }
  });

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);
};

/**
 * Enables arrow key nudging for absolutely positioned elements.
 * Each arrow key press moves the element by 1px (or 10px with Shift).
 * Respects the active offset origin for direction inversion (right/bottom).
 */
export const ArrowKeyPositionNudge = (props: Props) => {
  useArrowKeyPositionNudge(props);
  return null;
};
